import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  FiUsers,
  FiBook,
  FiCalendar,
  FiBell,
  FiActivity,
  FiZap,
  FiSettings,
  FiHome,
  FiPieChart,
  FiUser,
  FiHeart,
  FiTarget,
  FiLayers,
  FiMapPin,
  FiMap,
  FiCheckCircle,
  FiX
} from "react-icons/fi";

type Role = "admin" | "school" | "student" | "parent";

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────
interface ShortcutAction {
  id: string;
  title: string;
  subtitle: string;
  icon: React.ElementType;
  route: string;
}

const ALL_ACTIONS: Record<Role, ShortcutAction[]> = {
  admin: [
    { id: "a-dash",  title: "Dashboard", subtitle: "Main admin overview", icon: FiHome, route: "/dashboard" },
    { id: "a-rep",   title: "Reports", subtitle: "Analytics & insights", icon: FiPieChart, route: "/reports" },
    { id: "a-emo",   title: "Emotions", subtitle: "Manage emotion master", icon: FiHeart, route: "/master/emotion" },
    { id: "a-zone",  title: "Zones", subtitle: "Manage emotional zones", icon: FiTarget, route: "/master/zone" },
    { id: "a-log",   title: "Log Mood", subtitle: "Mood logging master", icon: FiActivity, route: "/master/logmood" },
    { id: "a-sch",   title: "Schools", subtitle: "Manage schools", icon: FiHome, route: "/master/school" },
    { id: "a-cat",   title: "Categories", subtitle: "Content categories", icon: FiLayers, route: "/master/category" },
    { id: "a-city",  title: "Cities", subtitle: "City master data", icon: FiMapPin, route: "/master/city" },
    { id: "a-state", title: "States", subtitle: "State master data", icon: FiMap, route: "/master/state" },
  ],
  school: [
    { id: "s-dash",  title: "Dashboard", subtitle: "School overview", icon: FiHome, route: "/school/dashboard" },
    { id: "s-stu",   title: "Manage Students", subtitle: "View and manage student records", icon: FiUsers, route: "/school/students" },
    { id: "s-tea",   title: "Teachers", subtitle: "Manage school faculty", icon: FiUser, route: "/school/teachers" },
    { id: "s-cal",   title: "Calendar", subtitle: "View events and schedules", icon: FiCalendar, route: "/school/calendar" },
    { id: "s-doc",   title: "Documents", subtitle: "Manage school resources", icon: FiBook, route: "/school/documents" },
    { id: "s-not",   title: "Alerts", subtitle: "Critical notifications", icon: FiBell, route: "/school/notifications" },
    { id: "s-cls",   title: "Academic Setup", subtitle: "Configure classes and subjects", icon: FiBook, route: "/school/academics/classes" },
    { id: "s-div",   title: "Divisions", subtitle: "Manage class divisions", icon: FiLayers, route: "/school/academics/divisions" },
  ],
  student: [
    { id: "st-dash", title: "Dashboard", subtitle: "My wellbeing overview", icon: FiHome, route: "/student/dashboard" },
    { id: "st-log",  title: "Log Mood", subtitle: "Check in your emotions", icon: FiHeart, route: "/student/log-mood" },
    { id: "st-hist", title: "History", subtitle: "Past mood logs", icon: FiActivity, route: "/student/mood-history" },
    { id: "st-cal",  title: "Calendar", subtitle: "My events & tasks", icon: FiCalendar, route: "/student/calendar" },
    { id: "st-str",  title: "Streaks & Rewards", subtitle: "View achievements", icon: FiZap, route: "/student/streaks-rewards" },
    { id: "st-not",  title: "Alerts", subtitle: "My notifications", icon: FiBell, route: "/student/notifications" },
    { id: "st-com",  title: "Community", subtitle: "Connect with peers", icon: FiUsers, route: "/student/community" },
    { id: "st-prof", title: "Profile", subtitle: "Manage my account", icon: FiUser, route: "/student/profile" },
  ],
  parent: [
    { id: "p-dash",  title: "Dashboard", subtitle: "Family overview", icon: FiHome, route: "/parent/dashboard" },
    { id: "p-child", title: "My Children", subtitle: "Monitor wellbeing", icon: FiUsers, route: "/parent/children" },
    { id: "p-rep",   title: "Reports", subtitle: "Detailed insights", icon: FiPieChart, route: "/parent/reports" },
    { id: "p-cal",   title: "Calendar", subtitle: "Family schedule", icon: FiCalendar, route: "/parent/calendar" },
    { id: "p-prof",  title: "Profile", subtitle: "Manage account", icon: FiUser, route: "/parent/profile" },
    { id: "p-not",   title: "Alerts", subtitle: "Critical updates", icon: FiBell, route: "/parent/notifications" },
  ],
};

const DEFAULT_PINNED: Record<Role, string[]> = {
  admin:   ["a-dash", "a-rep", "a-emo", "a-zone"],
  school:  ["s-stu", "s-cls", "s-cal"],
  student: ["st-dash", "st-log", "st-hist", "st-cal"],
  parent:  ["p-child", "p-rep", "p-cal", "p-not"],
};

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────
const storageKey = (role: Role) => `sidebar_pinned_${role}`; // Share storage with SidebarQuickActions
const enabledKey = "shortcut_enabled_v3";

function getPinned(role: Role): string[] {
  try {
    const raw = localStorage.getItem(storageKey(role));
    if (raw) return JSON.parse(raw);
  } catch {}
  return DEFAULT_PINNED[role];
}
function setPinned(role: Role, ids: string[]) {
  localStorage.setItem(storageKey(role), JSON.stringify(ids));
}

// ─────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────
const FloatingShortcut: React.FC = () => {
  const { userType } = useAuth();
  const navigate = useNavigate();
  const role: Role = (userType as Role) || "admin";

  const [enabled, setEnabled] = useState<boolean>(() => {
    const v = localStorage.getItem(enabledKey);
    return v === null ? true : v === "true";
  });

  const [pinned, setPinnedState] = useState<string[]>(() => getPinned(role));
  const [open, setOpen] = useState(false);
  const [showCustomise, setShowCustomise] = useState(false);
  const [selectedInModal, setSelectedInModal] = useState<string[]>([]);

  // Draggable state
  const [pos, setPos] = useState({ x: window.innerWidth - 72, y: window.innerHeight - 100 });
  const dragging = useRef(false);
  const startRef = useRef({ mx: 0, my: 0, bx: 0, by: 0 });
  const moved = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    localStorage.setItem(enabledKey, String(enabled));
  }, [enabled]);

  // Sync pinned state in case they change it from the sidebar
  useEffect(() => {
    const handleStorageChange = () => setPinnedState(getPinned(role));
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [role]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragging.current = true;
    moved.current = false;
    startRef.current = { mx: e.clientX, my: e.clientY, bx: pos.x, by: pos.y };
  }, [pos]);

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging.current) return;
    const dx = e.clientX - startRef.current.mx;
    const dy = e.clientY - startRef.current.my;
    if (Math.abs(dx) > 5 || Math.abs(dy) > 5) moved.current = true;
    const newX = Math.max(36, Math.min(window.innerWidth - 36, startRef.current.bx + dx));
    const newY = Math.max(36, Math.min(window.innerHeight - 36, startRef.current.by + dy));
    setPos({ x: newX, y: newY });
  }, []);

  const onPointerUp = useCallback(() => {
    dragging.current = false;
    if (!moved.current) setOpen((o) => !o);
  }, []);

  const handleAction = (route: string) => {
    setOpen(false);
    navigate(route);
  };

  const openCustomise = () => {
    setOpen(false);
    setSelectedInModal([...pinned]);
    setShowCustomise(true);
  };

  const toggleModalAction = (id: string) => {
    setSelectedInModal((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 4) return prev;
      return [...prev, id];
    });
  };

  const savePinned = () => {
    setPinned(role, selectedInModal);
    setPinnedState(selectedInModal);
    window.dispatchEvent(new Event("storage")); // Notify SidebarQuickActions
    setShowCustomise(false);
  };

  const allActions = ALL_ACTIONS[role];
  const pinnedActions = pinned
    .map((id) => allActions.find((a) => a.id === id))
    .filter(Boolean) as ShortcutAction[];

  if (!enabled) {
    return (
      <button
        onClick={() => setEnabled(true)}
        className="fixed bottom-5 right-5 z-[99999] bg-slate-900/85 backdrop-blur-md text-white border border-white/10 rounded-full px-4 py-2 text-sm font-semibold cursor-pointer shadow-xl hover:bg-slate-800 transition-colors flex items-center gap-2"
      >
        <span className="text-base">⚡</span> Enable Shortcuts
      </button>
    );
  }

  const popupAbove = pos.y > window.innerHeight / 2;
  const isRightSide = pos.x > window.innerWidth / 2;

  return (
    <>
      <div
        ref={containerRef}
        style={{
          position: "fixed",
          left: pos.x,
          top: pos.y,
          transform: "translate(-50%, -50%)",
          zIndex: 99998,
          userSelect: "none",
          touchAction: "none",
        }}
      >
        {open && (
          <div
            style={{
              position: "absolute",
              ...(popupAbove
                ? { bottom: "calc(100% + 16px)", top: "auto" }
                : { top: "calc(100% + 16px)", bottom: "auto" }),
              ...(isRightSide
                ? { right: "-10px", left: "auto" }
                : { left: "-10px", right: "auto" }),
              transformOrigin: `${isRightSide ? 'right' : 'left'} ${popupAbove ? 'bottom' : 'top'}`,
              background: "#fff",
              borderRadius: "20px",
              padding: "20px",
              boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
              width: "280px",
              animation: "popInScale 0.2s cubic-bezier(0.34,1.56,0.64,1)",
              zIndex: 99999,
            }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider m-0">
                Quick Actions
              </h3>
              <button
                onClick={openCustomise}
                className="text-gray-400 hover:text-[#1ecab8] p-1 rounded transition-colors"
                title="Customise Shortcuts"
              >
                <FiSettings size={16} />
              </button>
            </div>

            <div className="space-y-2">
              {pinnedActions.map((action) => {
                const Icon = action.icon;
                return (
                  <div
                    key={action.id}
                    onClick={() => handleAction(action.route)}
                    className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 bg-white cursor-pointer hover:border-[#1ecab8] hover:bg-teal-50 transition-all group"
                  >
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gray-50 text-gray-500 group-hover:bg-[#1ecab8]/10 group-hover:text-[#1ecab8] transition-colors">
                      <Icon size={18} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 text-sm m-0">{action.title}</h4>
                      <p className="text-xs text-gray-500 m-0">{action.subtitle}</p>
                    </div>
                  </div>
                );
              })}
              
              {pinnedActions.length < 4 && (
                <div 
                  onClick={openCustomise}
                  className="flex items-center justify-center gap-2 p-3 rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 text-center cursor-pointer hover:bg-gray-100 hover:border-gray-300 transition-colors"
                >
                  <span className="text-gray-400 font-bold">+</span>
                  <span className="text-sm text-gray-500 font-medium">Add Action</span>
                </div>
              )}
            </div>

            <div className="mt-4 pt-3 border-t border-gray-100">
              <button
                onClick={() => { setOpen(false); setEnabled(false); }}
                className="w-full py-2 px-4 rounded-lg bg-red-50 text-red-600 text-xs font-bold hover:bg-red-100 transition-colors"
              >
                Hide Floating Button
              </button>
            </div>

            {/* Tooltip arrow */}
            <div
              style={{
                position: "absolute",
                ...(isRightSide ? { right: "31px" } : { left: "31px" }),
                transform: "rotate(45deg)",
                width: 14,
                height: 14,
                background: "#fff",
                ...(popupAbove
                  ? { bottom: -7, boxShadow: "4px 4px 10px rgba(0,0,0,0.05)" }
                  : { top: -7, boxShadow: "-4px -4px 10px rgba(0,0,0,0.05)" }),
              }}
            />
          </div>
        )}

        {/* Floating Button */}
        <div
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          className="w-14 h-14 rounded-full flex items-center justify-center cursor-grab shadow-lg transition-transform hover:scale-105"
          style={{
            background: open ? "#fff" : "linear-gradient(135deg, rgba(30, 202, 184, 0.25), rgba(27, 184, 166, 0.25))",
            backdropFilter: "none",
            color: open ? "#1ecab8" : "#fff",
            border: open ? "2px solid #1ecab8" : "1px solid rgba(255, 255, 255, 0.3)",
          }}
        >
          {open ? <FiX size={24} /> : <FiZap size={24} />}
        </div>
      </div>

      {/* Customise Modal */}
      {showCustomise && (
        <div
          className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-gray-900/40 backdrop-blur-sm"
          onClick={(e) => e.target === e.currentTarget && setShowCustomise(false)}
        >
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-gray-900 m-0">Customise Shortcuts</h2>
                <p className="text-sm text-gray-500 m-0">Select up to 4 quick actions</p>
              </div>
              <button
                onClick={() => setShowCustomise(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
              >
                <FiX size={20} />
              </button>
            </div>

            <div className="p-6 max-h-[60vh] overflow-y-auto space-y-3">
              {allActions.map((action) => {
                const isSelected = selectedInModal.includes(action.id);
                const isDisabled = !isSelected && selectedInModal.length >= 4;
                return (
                  <div
                    key={action.id}
                    onClick={() => !isDisabled && toggleModalAction(action.id)}
                    className={`flex items-center justify-between p-3 rounded-xl border-2 transition-all ${
                      isSelected
                        ? "border-[#1ecab8] bg-[#1ecab8]/5 cursor-pointer"
                        : isDisabled
                        ? "border-transparent bg-gray-50 opacity-50 cursor-not-allowed"
                        : "border-transparent bg-gray-50 hover:bg-gray-100 cursor-pointer"
                    }`}
                  >
                    <div>
                      <h4 className="font-semibold text-gray-900 text-sm m-0">{action.title}</h4>
                      <p className="text-xs text-gray-500 m-0">{action.subtitle}</p>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                      isSelected ? "border-[#1ecab8] bg-[#1ecab8]" : "border-gray-300"
                    }`}>
                      {isSelected && <FiCheckCircle size={12} className="text-white" />}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3">
              <button
                onClick={() => setShowCustomise(false)}
                className="px-4 py-2 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={savePinned}
                disabled={selectedInModal.length === 0}
                className="px-6 py-2 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-[#1ecab8] to-[#1bb8a6] hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
      <style>{`
        @keyframes popInScale {
          from { transform: scale(0.9); opacity: 0; }
          to   { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </>
  );
};

export default FloatingShortcut;
